<script setup>
    const props = defineProps({
        headline: {
            type: String,
            required: true,
        },
        subHead: {
            type: String,
            required: false,
        },
        subText: { type: String, required: true },
        buttonText: {
            type: String,
            required: true,
        },
        pageTitle: {
            type: String,
            required: true,
        },
        container: {
            type: Boolean,
            default: false,
        },
        footer: {
            type: Boolean,
            default: false,
        },
        reducePadding: {
            type: Boolean,
            default: false,
        },
    });

    const errorRef = ref(null);
    const errorHeight = ref(0);
    const emailRegex = /.+?@.+?\..{2,}/;

    const submitted = ref(false);
    const submittedMessage = ref('');

    const config = useRuntimeConfig();
    const baseURL = config.public.clientFrontEnd;
    const route = useRoute();

    const hubspotPortalId = '146726';
    const hubspotFormId = 'bbc39d17-c359-4890-b3ba-c487a27ed366';
    const hubspotCookie = useCookie('hubspotutk');

    const hasError = ref(false);
    const honeypotValue = ref('');
    const submit = async () => {
        if (!emailValue.value) {
            hasError.value = true;
            return;
        }
        const { data: responseData } = await useFetch(`https://api.hsforms.com/submissions/v3/integration/submit/${hubspotPortalId}/${hubspotFormId}`, {
            method: 'post',
            body: {
                fields: [
                    {
                        objectTypeId: '0-1',
                        name: 'email',
                        value: emailValue.value,
                    },
                    {
                        objectTypeId: '0-1',
                        name: 'special_email_capture',
                        value: honeypotValue.value,
                    },
                ],
                context: {
                    hutk: hubspotCookie.value, // include this parameter and set it to the hubspotutk cookie value to enable cookie tracking on your submission
                    pageUri: `${baseURL}${route.fullPath}`,
                    pageName: props.pageTitle || document.title,
                },
            },
        });
        submittedMessage.value = responseData.value.inlineMessage;
        submitted.value = true;
        setTimeout(() => {
            submitted.value = false;
        }, 10000);
    };

    const emailValue = ref(null);
    const optOut = ref(false);
    const resubbed = ref(false);
    const resubFail = ref(false);

    const resubEmail = async () => {
        const pageUri = `${baseURL}${route.fullPath}`;
        const pageName = props.pageTitle || document.title;

        const response = await $fetch(`https://forms.hsforms.com/emailcheck/v1/form-resubscribe`, {
            method: 'post',
            body: emailValue.value,
            query: {
                hs_static_app: 'forms-embed',
                hs_static_app_version: 1.5387,
                'X-HubSpot-Static-App-Info': 'forms-embed-1.5387',
                portalId: 146726,
                formId: 'bbc39d17-c359-4890-b3ba-c487a27ed366',
                resub_form_name: pageName,
                resub_form_address: pageUri,
            },
        });
        if (response.success) {
            resubbed.value = true;
        } else {
            resubFail.value = true;
        }
    };

    watch(emailValue, async (newValue) => {
        if (emailRegex.test(newValue)) {
            try {
                const response = await $fetch('/api/v1/subs', {
                    method: 'GET',
                    query: {
                        email: newValue,
                    },
                });
                if (response.result == true) {
                    optOut.value = true;
                } else {
                    optOut.value = false;
                }
            } catch (e) {
                // console.log(e);
            }
        } else {
            optOut.value = false;
        }
    });

    watch(optOut, (newValue) => {
        if (newValue) {
            setTimeout(() => {
                errorHeight.value = errorRef.value.offsetHeight;
            }, 16);
        } else {
            errorHeight.value = 0;
        }
    });
</script>

<template>
    <section
        class="block-newsletter-signup"
        tabindex="-1"
        :class="{
            'px-(--box-padding-lr-m) lg:px-(--box-padding-lr)': container,
            'mb-[60px]': !footer,
            'm-0 p-0': reducePadding,
        }">
        <div :class="container ? 'inner-container' : 'px-(--box-padding-lr-m) lg:px-(--box-padding-lr)'">
            <div class="not-footer-box flex flex-col gap-[18px] w-full">
                <div v-if="!submitted">
                    <div class="flex flex-col min-[1060px]:flex-row min-[1060px]:items-center gap-[17px] min-[1060px]:gap-[2.4375rem]">
                        <MessHtml
                            tag="p"
                            :html="headline"
                            class="font-hubspot-serif font-normal tracking-normal text-[calc(21/var(--base-fs)*1rem)] leading-[1.5] tracking-[0.42px] lg:text-[calc(23/var(--base-fs)*1rem)] min-[1060px]:flex-[1_1_50%]" />
                    <div class="min-[1060px]:flex-[1_1_50%]">
                        <form class="relative flex items-center justify-between
                            h-[53px] w-full px-[24px] py-[10px]
                            border border-[var(--pink)] rounded-[50px]
                            transition-colors duration-200 focus-within:border-white"
                            @submit.prevent="submit"
                            v-posthog-capture:submit="'newsletter_signup_submitted'">
                            <label for="email" class="sr-only">Email address</label>
                            <!-- Honeypot — hidden from real users, bots will fill it. HubSpot uses
                                 special_email_capture to mark these submissions as spam. -->
                            <div aria-hidden="true" class="absolute left-[-9999px] top-[-9999px] w-[1px] h-[1px] overflow-hidden">
                                <label for="newsletter-email-confirm">Don't fill this out if you're human</label>
                                <input id="newsletter-email-confirm" type="text" name="special_email_capture"
                                    tabindex="-1" autocomplete="off" v-model="honeypotValue" />
                            </div>
                            <input
                                name="email"
                                id="email"
                                type="email"
                                v-model="emailValue"
                                placeholder="Enter your email address"
                                class="bg-transparent border-none outline-none w-full
                                    font-hubspot-sans font-light
                                    text-[calc(16/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.32px]
                                    text-[var(--pink)] placeholder:text-[var(--pink)]
                                    transition-colors duration-200 focus:text-white" />
                            <button type="submit" :aria-label="buttonText"
                                class="group shrink-0 ml-[10px]
                                    focus-visible:outline-none focus-visible:ring-2
                                    focus-visible:ring-[var(--pink)] focus-visible:rounded-full">
                                <img src="/img/2026/footer/arrow.svg" loading="lazy" decoding="async" alt="" aria-hidden="true"
                                    class="size-[13px] -scale-x-100 transition-[filter] duration-200 group-hover:brightness-[3]" />
                            </button>
                        </form>
                        <p v-if="hasError" class="font-hubspot-sans font-light text-[calc(13/var(--base-fs)*1rem)] text-white mt-2">
                            *Please enter a valid email address.
                        </p>
                        <p v-if="optOut && !resubbed" ref="errorRef" class="font-hubspot-sans font-light text-[calc(13/var(--base-fs)*1rem)] text-white mt-2">
                            <button class="error underline" @click.prevent="resubEmail" v-posthog-capture="'newsletter_resubscribe_clicked'">
                                Looks like you've opted out of email communication. Click here to get an email and opt back in.
                            </button>
                        </p>
                        <p v-if="resubbed" class="font-hubspot-sans font-light text-[calc(13/var(--base-fs)*1rem)] text-white mt-2">Please check your email to opt back in.</p>
                        <p v-if="resubFail" class="font-hubspot-sans font-light text-[calc(13/var(--base-fs)*1rem)] text-white mt-2">Something went wrong. Please refresh the page and try again.</p>
                    </div>
                    </div>
                    <MessHtml tag="div" v-if="subText" :html="subText" class="subText text-[11px] leading-[18px] tracking-[0.22px] mt-6" />
                </div>
                <div v-else class="flex items-center justify-center py-4">
                    <MessHtml tag="p" :html="submittedMessage" class="font-hubspot-serif font-normal text-[calc(21/var(--base-fs)*1rem)] leading-[1.5] text-white" />
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
    /* [child] — styles targeting .not-footer-box */
    .block-newsletter-signup:not(.m-0) :deep(.not-footer-box) {
        border-radius: var(--radius);
        padding: var(--box-padding-tb-m) var(--box-padding-lr-m);
        margin-bottom: var(--box-padding-tb-m);
        background: linear-gradient(127deg, var(--magenta, #46062b) 13%, var(--purple, #820ca7) 95%);
    }

    @media (min-width: 1024px) {
        .block-newsletter-signup:not(.m-0) :deep(.not-footer-box) {
            padding: 3.5rem;
            margin-bottom: var(--box-padding-tb);
        }
    }

    /* [child] — h4 bottom margin inside flex container */
    .block-newsletter-signup :deep(h4) {
        margin-bottom: 12px;
    }

    @media (max-width: 1060px) {
        .block-newsletter-signup :deep(h4) {
            line-height: 1.2;
            margin-bottom: 1.4rem;
        }
    }

    /* [child] — h5 submitted message styling */
    .block-newsletter-signup:not(.m-0) :deep(h5) {
        color: white;
        font-size: calc(20 / var(--base-fs) * 1rem);
        font-weight: 500;
        line-height: 1.75rem;
        letter-spacing: 0.025rem;
        margin-bottom: 1.125rem;
    }

    @media (min-width: 1024px) {
        .block-newsletter-signup:not(.m-0) :deep(h5) {
            margin-top: 0;
        }
    }

    /* [child] — subText paragraph color and font */
    .block-newsletter-signup:not(.m-0) :deep(.subText p) {
        color: white;
        font-family: var(--font-hubspot-sans);
        letter-spacing: 0;
    }

    /* [child] — mess-html text color in not-footer context */
    .block-newsletter-signup:not(.m-0) :deep(.mess-html) {
        color: white;
    }


    /* [child] — subText anchor styling with hover state */
    .subText :deep(a) {
        color: white;
        border-bottom: none;
        text-decoration: underline dotted white;
        text-underline-offset: 3px;
        text-decoration-thickness: 1px;
    }

    /* [state] — subText anchor hover */
    .subText :deep(a:hover) {
        color: white;
        text-decoration: none;
    }

    /* [child] — mobile inner-container padding */
    @media (max-width: 768px) {
        .inner-container {
            padding: 0 1.5rem;
        }
    }

    /* [child] — mobile h6 font size */
    @media (max-width: 768px) {
        .block-newsletter-signup :deep(.h6) {
            font-size: 10px;
        }
    }
</style>
